/**
 * CSS column-reverse 기반 RoomContent
 * 
 * 장점:
 * - 라이센스 불필요 (순수 CSS)
 * - prepend 시 스크롤 위치 자동 유지
 * - 구현 간단
 * 
 * 단점:
 * - 가상화 없음 → 수백 개 메시지 시 성능 저하
 * - DOM에 모든 메시지 렌더링
 */

import { Trans, t } from '@lingui/macro';
import { getDisplayName, resolveMessageUser } from '../../services/Utils/userDisplayName';
import { Loader2, RefreshCw, X } from 'lucide-react';
import { memo, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useAppUIContext } from '../../context/AppUIContext';
import { Message, SendFile } from '../../models/Message';
import { Room } from '../../models/Room';
import { RoomToken } from '../../models/RoomToken';
import { Fire } from '../../services/FireClient';
import { FireClientMsg } from '../../services/FireClientMsg';
import { MessageLoader } from '../../services/MessageLoader';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { UtilApp } from '../../services/Utils/UtilApp';
import { Util } from '../../services/Utils/Util';
import { UIMessage } from '../components/UIMessage';
import { MessageItemState } from '../components/MessageItem';
import MsgInputFooter from '../components/message-inputs/MsgInputFooter';
import { RoomRefreshEmitter, RoomViewState } from './RoomEvents';
import { RoomWelcomeMessage } from './RoomWelcomeMessage';
import { Network } from "@capacitor/network";
import { PushService } from '../../services/Utils/PushService';
import { Capacitor } from "@capacitor/core";
import { useMessageContext } from '../main/NotificationModal';
import { Notification } from "../../models/Notification";
import { UserSimplified } from "../../models/User";
import { useHistory } from '../../hooks/useRouterCompat';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { UserAvatar } from '../components/UserAvatar';
import { RoomMessageItem } from './RoomMessageItem';
import { appLifecycle } from '../../services/Utils/AppLifecycle';
import { cn } from '../../lib/utils';
import { IconChevronDown } from '../components/FigmaIcons';

interface Props {
  room: Room;
  onProgressChange?: (inProgress: boolean) => void;
  contentWidth?: 'min-width' | 'full';
}

const RoomContent: React.FC<Props> = ({ room, onProgressChange, contentWidth = 'min-width' }) => {
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [state, setState] = useState<RoomViewState>('loading');
  const [currentMessage, setCurrentMessage] = useState<UIMessage | null>(null);
  const currentMessageRef = useRef<UIMessage | null>(null);
  currentMessageRef.current = currentMessage;
  const [noMoreMessages, setNoMoreMessages] = useState(false);
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [roomTokens, setRoomTokens] = useState<RoomToken[]>([]);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const { filter: type, soribots } = useAppUIContext();
  // 🔧 soribots를 ref로 전달: MsgInputFooter가 Context 구독 제거 → 리렌더 방지
  const soribotsRef = useRef(soribots);
  soribotsRef.current = soribots;
  const [progressForFile, setProgressForFile] = useState<{ [key: string]: SendFile }>({});
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [sendingMessage, setSendingMessage] = useState<{ [key: string]: UIMessage }>({});
  const sendingMessageRef = useRef<{ [key: string]: UIMessage }>({}); // 🔧 ref로도 관리 (클로저 문제 해결)
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const { notification, setNotification } = useMessageContext();
  const transitionRefs = useRef<Record<string, { current: HTMLDivElement | null }>>({});

  const messageLoader = useRef<MessageLoader | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  const cooldownRef = useRef(false);
  const networkListenerRef = useRef<any>(null);
  const isInitiatingRef = useRef(false); // 🔧 초기화 중복 실행 방지 가드
  const initiateStartTimeRef = useRef<number>(0); // 🔧 데드락 타임아웃용 시작 시간

  // 스레드 인라인 펼침 상태
  const [activeThreads, setActiveThreads] = useState<Set<string>>(new Set());
  const activeThreadsRef = useRef<Set<string>>(new Set()); // 🔧 ref로도 관리 (클로저 문제 해결)

  // 🔧 스레드 펼칠 때 원글을 화면 상단으로 스크롤
  const scrollToThreadTop = useCallback((id: string) => {
    // 렌더링 완료 후 스크롤 (스레드 확장 애니메이션 대기)
    setTimeout(() => {
      const element = messageRefs.current[id];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  }, []);

  const toggleActiveThread = useCallback((id: string) => {

    // 🔧 ref 먼저 업데이트 (즉시 반영)
    const isOpening = !activeThreadsRef.current.has(id);

    // 스레드를 닫을 때: 부모 메시지의 현재 화면 위치 저장
    let messageElement: HTMLElement | null = null;
    let messageTopBeforeClose = 0;

    if (!isOpening && scrollContainerRef.current) {
      // 메시지 요소 찾기
      messageElement = scrollContainerRef.current.querySelector(`[data-message-id="${id}"]`) as HTMLElement;
      if (messageElement) {
        // 뷰포트 기준 현재 위치 저장
        messageTopBeforeClose = messageElement.getBoundingClientRect().top;
      }
    }

    if (activeThreadsRef.current.has(id)) {
      activeThreadsRef.current.delete(id);
    } else {
      activeThreadsRef.current.add(id);
    }


    // state도 업데이트 (리렌더링 트리거)
    setActiveThreads(new Set(activeThreadsRef.current));

    // 스레드를 열 때만 스크롤
    if (isOpening) {
      scrollToThreadTop(id);
    } else if (messageElement && scrollContainerRef.current) {
      // 스레드를 닫을 때: 메시지의 화면 위치 유지
      requestAnimationFrame(() => {
        if (messageElement && scrollContainerRef.current) {
          const messageTopAfterClose = messageElement.getBoundingClientRect().top;
          const scrollDelta = messageTopAfterClose - messageTopBeforeClose;

          // column-reverse에서는 scrollTop이 음수 방향이므로 반대로 조정
          scrollContainerRef.current.scrollTop += scrollDelta;
        }
      });
    }
  }, [scrollToThreadTop]);

  const addActiveThread = useCallback((id: string) => {
    const isOpening = !activeThreadsRef.current.has(id);

    // ref 먼저 업데이트
    activeThreadsRef.current.add(id);

    // state도 업데이트
    setActiveThreads(new Set(activeThreadsRef.current));

    // 스레드를 열 때만 스크롤
    if (isOpening) {
      scrollToThreadTop(id);
    }
  }, [scrollToThreadTop]);

  const [mentionTarget, setMentionTarget] = useState<{ user: UserSimplified, messageId: string, threadId: string } | null>(null);

  // 토스트 큐
  const [toastQueue, setToastQueue] = useState<Message[]>([]);
  const toastQueueRef = useRef<Message[]>([]);
  const shownToastIdsRef = useRef<Set<string>>(new Set());
  const threadToast = toastQueue[0] || null;
  const addToast = (m: Message) => {
    if (shownToastIdsRef.current.has(m.id)) return;
    // 브라우저가 백그라운드이면 토스트 표시 안 함 (푸시 알림으로 충분)
    if (document.visibilityState === 'hidden') return;
    // 같은 스레드에 아직 확인하지 않은 토스트가 큐에 있으면 추가 안 함
    if (m.parentMessageId) {
      const hasPendingForThread = toastQueueRef.current.some(t => t.parentMessageId === m.parentMessageId);
      if (hasPendingForThread) return;
    }
    shownToastIdsRef.current.add(m.id);
    toastQueueRef.current = [...toastQueueRef.current, m];
    setToastQueue(toastQueueRef.current);
  };
  const dismissToast = () => {
    toastQueueRef.current = toastQueueRef.current.slice(1);
    setToastQueue(toastQueueRef.current);
  };
  const [highlightedReaction, setHighlightedReaction] = useState<{ messageId: string; emoticon: string } | null>(null);

  // inProgress 변경 시 부모에게 알림
  useEffect(() => {
    onProgressChange?.(inProgress);
  }, [inProgress, onProgressChange]);

  // 알림 리셋 (onNewMessages에서 사용하기 위해 먼저 정의)
  // ref로 최신 함수 유지 (onNewMessages 의존성 체인 방지)
  const resetNotificationsForRoomRef = useRef<() => Promise<void>>();

  const resetNotificationsForRoom = useCallback(async () => {
    const isFg = await UtilApp.isForeground();

    if (isFg) {
      setTimeout(() => {
        if (Capacitor.isNativePlatform()) {
          PushService.removeNotificationsByRoomId(room.id);
        }

        const allNotifs = MyNotifs.NotificationsByRoom.get(room.id);
        const newNotifs = allNotifs?.filter(n => n.isNew && n.type != 'CallStart');

        if (newNotifs && newNotifs.length > 0) {
          const toClear = newNotifs.filter(notif => {
            return messageLoader.current?.allMessages.some(m => m.id == notif.messageId) ?? false;
          });

          if (toClear.length > 0) {
            Fire.clearNotifications(toClear);
          }
        }
      }, 150);
    }
  }, [room.id]);

  // ref에 최신 함수 저장
  useEffect(() => {
    resetNotificationsForRoomRef.current = resetNotificationsForRoom;
  }, [resetNotificationsForRoom]);

  // 새 메시지 콜백 - 기존 RoomContent 로직 적용
  // 🔧 sendingMessageRef.current를 사용하여 항상 최신 값 접근 (클로저 문제 해결)
  const onNewMessages = useCallback((_messages: UIMessage[], newParentMessages: boolean, visibleMsgs: Message[], invisibleMsgs: Message[]) => {
    const currentSendingMessage = sendingMessageRef.current;



    // sendingMessage 정리 - 서버에서 확인된 메시지는 sending 상태 제거
    _messages.forEach(m => {
      if (currentSendingMessage[m.msg.id]) {

        delete sendingMessageRef.current[m.msg.id];
        setSendingMessage((prev) => {
          const { [m.msg.id]: _, ...rest } = prev;
          return rest;
        });
      }
      // 자식 메시지도 체크
      m.children.forEach(c => {
        if (currentSendingMessage[c.msg.id]) {

          delete sendingMessageRef.current[c.msg.id];
          setSendingMessage((prev) => {
            const { [c.msg.id]: _, ...rest } = prev;
            return rest;
          });
        }
      });
    });

    // 불변성 유지: 새 배열로 sendingMessage 병합
    let updatedMessages = [..._messages];

    // 🔧 _messages 내의 isLocal=true 메시지들을 isLocal=false로 변경
    // (서버에서 메시지가 도착했으므로 로컬 상태 해제)
    // 3단계 깊이까지 탐색: 부모 → 자식(댓글) → 손자(리액션)
    updatedMessages = updatedMessages.map(m => {
      let needsUpdate = false;

      // 부모 메시지 확인
      let updatedParent = m;
      if (m.isLocal) {
        updatedParent = m.withLocalStatus(false);
        needsUpdate = true;
      }

      // 자식 메시지 확인 + 손자(grandchild) 리액션 확인
      const updatedChildren = updatedParent.children.map(c => {
        let updatedChild = c;
        if (c.isLocal) {
          updatedChild = c.withLocalStatus(false);
          needsUpdate = true;
        }

        // 🔧 손자 메시지 (댓글에 달린 리액션) isLocal 해제
        const hasLocalGrandchild = updatedChild.children.some(gc => gc.isLocal);
        if (hasLocalGrandchild) {
          needsUpdate = true;
          const updatedGrandchildren = updatedChild.children.map(gc =>
            gc.isLocal ? gc.withLocalStatus(false) : gc
          );
          updatedChild = updatedChild.withChildren(updatedGrandchildren);
        }

        return updatedChild;
      });

      if (needsUpdate) {
        return updatedParent.withChildren(updatedChildren);
      }
      return m;
    });

    // 🔧 ref에서 최신 값 읽기
    Object.keys(sendingMessageRef.current).forEach(key => {
      const uimsg = sendingMessageRef.current[key];
      if (uimsg.msg.roomId === room.id) {
        const m = uimsg.msg;

        // 이미 존재하는지 확인
        const existsInParent = updatedMessages.some(msg => msg.msg.id === m.id);
        const existsInChildren = updatedMessages.some(msg =>
          msg.children.some(c => c.msg.id === m.id)
        );

        if (existsInParent || existsInChildren) {
          return; // 이미 존재하면 스킵
        }

        if (m.parentMessageId) {
          // 부모를 새 객체로 교체 (불변성 유지)
          updatedMessages = updatedMessages.map(parentMsg => {
            if (parentMsg.msg.id === m.parentMessageId) {
              return parentMsg.withChild(uimsg);
            }
            return parentMsg;
          });
        } else {
          updatedMessages = [...updatedMessages, uimsg];
        }
      }
    });

    // 🔧 Toast for invisible messages (early return 전에 먼저 처리!)
    invisibleMsgs.forEach(m => {
      if (m.parentMessageId && m.user.id !== My.user.id) {
        addToast(m);
      }
    });

    // visibleMsgs가 없고 sendingMessage도 없으면 업데이트 스킵
    if (visibleMsgs.length === 0 && Object.keys(sendingMessageRef.current).length === 0) {
      return;
    }

    setMessages(updatedMessages);

    // 🔧 visibleMsgs 중 스레드가 펼쳐져 있지 않은 댓글도 토스트 표시
    visibleMsgs.forEach(m => {
      if (m.parentMessageId && m.user.id !== My.user.id) {
        // 스레드가 펼쳐져 있지 않으면 토스트 표시
        if (!activeThreadsRef.current.has(m.parentMessageId)) {
          addToast(m);
        }
      }
    });

    // 🔧 하단으로 스크롤 (새 메시지가 도착하고 하단에 있을 때)
    if (newParentMessages && scrollContainerRef.current) {
      // column-reverse에서는 scrollTop = 0이 하단
      scrollContainerRef.current.scrollTop = 0;
    }

    // 🔧 새 메시지 도착 시 알림 자동 클리어 (현재 방을 보고 있으므로)
    if (visibleMsgs.length > 0 || invisibleMsgs.length > 0) {
      // 비동기로 알림 클리어 (UI 렌더링 우선)
      setTimeout(() => {
        resetNotificationsForRoomRef.current?.();
      }, 100);
    }
  }, [room.id]); // resetNotificationsForRoom을 ref로 접근하므로 의존성 제거

  // 초기화
  const initiate = useCallback(async () => {
    // 🔧 데드락 방지: 10초 이상 initiate가 진행 중이면 강제 리셋
    if (isInitiatingRef.current) {
      const elapsed = Date.now() - (initiateStartTimeRef.current || 0);
      if (elapsed < 10000) {
        console.warn('[ROOM] initiate already in progress, skipping (elapsed:', elapsed, 'ms)');
        return;
      }
      console.warn('[ROOM] ⚠️ initiate stuck for', elapsed, 'ms — force resetting guard');
      isInitiatingRef.current = false;
    }

    isInitiatingRef.current = true;
    initiateStartTimeRef.current = Date.now();
    setState('loading');
    setNoMoreMessages(false);
    setMessages([]);
    setHighlightedId(null);
    setActiveThreads(new Set());

    try {
      messageLoader.current?.unsubscribe();
      messageLoader.current = new MessageLoader(room.id, onNewMessages);

      // 🔧 limitCount를 최대 10으로 제한 (과도한 getChildSummary 쿼리 방지)
      const limitCount = Math.min(Math.ceil(window.innerHeight / 80) + 2, 10);
      const t0 = performance.now();
      await messageLoader.current.loadOlderMessageAndSubscribeToNew(null, limitCount);
      console.log(`⏱️ [ROOM] initiate total: ${(performance.now() - t0).toFixed(0)}ms (room: ${room.id.substring(0, 8)}...)`);

      const uimsgs = messageLoader.current.getUIMessages();
      setMessages(uimsgs);

      setState('ready');
      // 🔧 ref를 통해 최신 함수 호출 (의존성 체인 방지)
      resetNotificationsForRoomRef.current?.();
    } catch (error) {
      console.error('[ROOM] initiate failed:', error);
      setState('error');
    } finally {
      isInitiatingRef.current = false;
      initiateStartTimeRef.current = 0;
    }
  }, [room.id, onNewMessages]); // resetNotificationsForRoom은 ref로 접근하므로 의존성 제거

  // 🔧 이전 메시지 열람 권한 실시간 갱신 감지
  const prevCanReadRef = useRef<boolean>(room.canReadPastMessages?.includes(My.user.id) || false);
  useEffect(() => {
    const canReadNow = room.canReadPastMessages?.includes(My.user.id) || false;
    if (canReadNow && !prevCanReadRef.current) {
      console.log("[ROOM] Access to past messages granted! Re-initiating MessageLoader...");
      // 권한이 생겼을 때 데이터를 과거권한 기준으로 다시 불러옵니다
      initiate();
    }
    prevCanReadRef.current = canReadNow;
  }, [room.canReadPastMessages, initiate]);

  // Room 변경 또는 마운트 시 초기화
  useEffect(() => {
    initiate();

    // 메시지 삭제 등의 이벤트 발생 시 messages 상태 재계산
    const handleRoomRefresh = () => {
      setMessages(prev => prev
        // 삭제된 부모 메시지 필터링
        .filter(m => !My.DeletedMessages.get(m.msg.id))
        .map(m => {
          // 삭제된 자식 메시지(댓글)도 필터링
          const filteredChildren = m.children.filter(c => !My.DeletedMessages.get(c.msg.id));
          if (filteredChildren.length !== m.children.length) {
            return m.withChildren(filteredChildren);
          }
          return m;
        })
      );
    };

    RoomRefreshEmitter.on(room.id, handleRoomRefresh);

    // Network listener
    if (Capacitor.isNativePlatform()) {
      const setupNetworkListener = async () => {
        if (networkListenerRef.current) {
          networkListenerRef.current.remove();
        }

        // 🔧 마지막 연결 상태를 추적하여 불필요한 initiate 방지
        let lastConnected = true;

        networkListenerRef.current = await Network.addListener('networkStatusChange', (status) => {

          // 1. 끊겼다가 다시 연결된 경우에만 initiate 실행
          // 2. 현재 이미 로딩 중인 경우에는 무시
          if (status.connected && !lastConnected) {
            initiate();
          }

          lastConnected = status.connected;
        });
      };
      setupNetworkListener();
    }

    // 🔧 포그라운드 복귀 시 해당 방의 알림 자동 클리어
    // 같은 방에 있다가 홈 → 푸시 통지 터치로 돌아올 때 room.id가 변하지 않아
    // initiate()가 재실행되지 않는 문제를 해결
    const unsubLifecycle = appLifecycle.onStateChange((isBackground) => {
      if (!isBackground) {
        setTimeout(() => {
          resetNotificationsForRoomRef.current?.();
        }, 300);
      }
    });

    // 🔧 웹 브라우저: 탭 포커스 복귀 시 알림 클리어
    // appLifecycle은 네이티브 앱에서만 동작하므로, 웹에서는 별도로 처리
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          resetNotificationsForRoomRef.current?.();
        }, 300);
      }
    };
    if (!Capacitor.isNativePlatform()) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      messageLoader.current?.unsubscribe();
      RoomRefreshEmitter.off(room.id, handleRoomRefresh);
      unsubLifecycle();
      if (!Capacitor.isNativePlatform()) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      if (networkListenerRef.current) {
        networkListenerRef.current.remove();
      }
    };
  }, [room.id, initiate]);

  // 토큰 구독 (외부 API 메시지의 토큰 이름 해석용)
  useEffect(() => {
    const unsub = Fire.token.subscribeToRoomTokens(room.id, setRoomTokens);
    return () => unsub();
  }, [room.id]);

  // 이전 메시지 로드 (loadmore)
  const loadOlderMessages = useCallback(async () => {
    if (loadingMoreRef.current || cooldownRef.current || noMoreMessages || !messageLoader.current) return;

    loadingMoreRef.current = true;
    setInProgress(true);

    const nrOlderCount = await messageLoader.current.loadOlderMessageAndSubscribeToNew(
      null,
      FireClientMsg.NrMsgsPerLoad
    );

    setNoMoreMessages(nrOlderCount < FireClientMsg.NrMsgsPerLoad);

    const uimsgs = messageLoader.current.getUIMessages();
    setMessages(uimsgs);

    loadingMoreRef.current = false;
    setInProgress(false);

    // 연달아 호출되는 것을 방지하는 쿨다운
    cooldownRef.current = true;
    setTimeout(() => {
      cooldownRef.current = false;
    }, 500);
  }, [noMoreMessages]);

  // 스크롤 이벤트 핸들러
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    // column-reverse에서는 scrollTop이 음수 또는 0에 가까울 때가 "상단"
    // 실제로는 가장 오래된 메시지가 있는 위치
    const scrollBottom = container.scrollHeight + container.scrollTop - container.clientHeight;

    // 상단(오래된 메시지)에 가까우면 loadmore
    if (scrollBottom < 100 && !loadingMoreRef.current && !cooldownRef.current && !noMoreMessages && state === 'ready') {
      loadOlderMessages();
    }
  }, [loadOlderMessages, noMoreMessages, state]);



  // NodeRef 관리
  const getNodeRef = useCallback((id: string) => {
    if (!transitionRefs.current[id]) {
      transitionRefs.current[id] = { current: null };
    }
    return transitionRefs.current[id];
  }, []);

  // 파일 진행률
  const onProgressForFile = useCallback((key: string, sendFile: SendFile) => {
    setProgressForFile(prev => ({ ...prev, [key]: sendFile }));
  }, []);

  // 메시지 전송 완료 - sending 상태 제거 및 isLocal 업데이트
  const onSendedMessage = useCallback((uimsg: UIMessage) => {
    // ref도 함께 업데이트
    delete sendingMessageRef.current[uimsg.msg.id];
    setSendingMessage((prev) => {
      const newMsg = { ...prev };
      delete newMsg[uimsg.msg.id];
      return newMsg;
    });

    // 파일 진행률 상태 정리 (업로드 프리뷰 제거)
    setProgressForFile(prev => {
      const newProgress = { ...prev };
      const prefix = `${uimsg.msg.id}/`;
      Object.keys(newProgress).forEach(key => {
        if (key.startsWith(prefix)) {
          delete newProgress[key];
        }
      });
      return newProgress;
    });

    // 🔧 messages 상태에서 해당 메시지의 isLocal을 false로 업데이트
    // ⚠️ 기존 객체를 변이(mutate)하면 React.memo가 변경을 감지하지 못함!
    //    → 반드시 새 객체를 생성해야 함 (withLocalStatus 사용)
    setMessages((prev) => {
      const msgId = uimsg.msg.id;
      const parentId = uimsg.msg.parentMessageId;

      if (parentId) {
        // 자식 메시지(답글): 부모를 찾아서 해당 자식의 isLocal 업데이트
        // 🔧 grandchild(댓글에 달린 리액션)도 탐색
        return prev.map((parentMsg) => {
          if (parentMsg.msg.id === parentId) {
            // 1단계: 직접 자식에서 찾기
            let found = false;
            const updatedChildren = parentMsg.children.map((child) => {
              if (child.msg.id === msgId) {
                found = true;
                return child.withLocalStatus(false);
              }
              // 2단계: 손자(댓글의 리액션)에서 찾기
              const hasTarget = child.children.some(gc => gc.msg.id === msgId);
              if (hasTarget) {
                found = true;
                const updatedGrandchildren = child.children.map(gc =>
                  gc.msg.id === msgId ? gc.withLocalStatus(false) : gc
                );
                return child.withChildren(updatedGrandchildren);
              }
              return child;
            });
            if (found) {
              return parentMsg.withChildren(updatedChildren);
            }
          }
          return parentMsg;
        });
      } else {
        // 부모 메시지: 직접 isLocal 업데이트
        return prev.map((msg) => {
          if (msg.msg.id === msgId) {
            return msg.withLocalStatus(false);
          }
          return msg;
        });
      }
    });
  }, []);

  // 🔧 리액션 추가/제거 시 불변성을 유지하며 messages 상태 업데이트
  // MsgInputFooterReactions에서 직접 mutation 대신 이 콜백을 호출
  const handleReactionChildrenChange = useCallback((targetMessageId: string, updatedChildren: UIMessage[]) => {
    setMessages(prev => prev.map(m => {
      // Case 1: 최상위 메시지에 리액션한 경우 — 직접 children 교체
      if (m.msg.id === targetMessageId) {
        return m.withChildren(updatedChildren);
      }
      // Case 2: 댓글(reply)에 리액션한 경우 — children 내부에서 찾아 교체
      const childIdx = m.children.findIndex(c => c.msg.id === targetMessageId);
      if (childIdx >= 0) {
        const newChildren = m.children.map(c =>
          c.msg.id === targetMessageId ? c.withChildren(updatedChildren) : c
        );
        return m.withChildren(newChildren);
      }
      return m;
    }));
  }, []);

  // 불변성을 유지하면서 로컬 메시지를 추가한 새 배열 반환
  const pushLocalMessage = useCallback((currentMessages: UIMessage[], uimsg: UIMessage): UIMessage[] => {
    const m = uimsg.msg;
    if (m.parentMessageId) {
      // 자식 메시지(답글): 부모를 찾아서 자식 추가
      return currentMessages.map(parentMsg => {
        if (parentMsg.msg.id === m.parentMessageId) {
          return parentMsg.withChild(uimsg);
        }
        return parentMsg;
      });
    } else {
      // 부모 메시지: 새 배열에 추가
      return [...currentMessages, uimsg];
    }
  }, []);

  // 하단으로 스크롤
  const scrollViewToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      // column-reverse에서는 scrollTop = 0이 하단
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  // 로컬 메시지 추가 (sending 상태 포함)
  const addLocalMsg = useCallback((uimsg?: UIMessage) => {
    setCurrentMessage(null);
    if (!uimsg) return;

    try {
      const m = uimsg.msg;
      // sending 상태 추가 (ref와 state 둘 다)
      sendingMessageRef.current[m.id] = uimsg;
      setSendingMessage((prev) => ({ ...prev, [m.id]: uimsg }));
      // 메시지 추가 (답글이면 부모에, 아니면 배열 끝에)
      setMessages(prevMessages => pushLocalMessage(prevMessages, uimsg));

      if (!m.parentMessageId) {
        // 부모 메시지: 하단으로 스크롤
        scrollViewToBottom();
      } else {
        // 🔧 답글 전송 시 해당 스레드 자동 펼침
        addActiveThread(m.parentMessageId);
      }
    } catch (error) {
      console.error('Failed to add local message', error);
    }
  }, [pushLocalMessage, scrollViewToBottom, addActiveThread]);

  // 🔧 MsgInputFooter용 안정된 콜백 (ref로 최신 currentMessage 접근)
  const targetMessageRef = useRef<typeof targetMessage | null>(null);
  const handleScrollToParent = useCallback(() => {
    const msg = currentMessageRef.current;
    if (msg) {
      targetMessageRef.current?.(msg.msg.id, msg.msg.parentMessageId ?? null);
    }
  }, []);

  // 자식 메시지 로드 - messages state도 업데이트
  const handleLoadChildren = useCallback((parentId: string, children: Message[]) => {
    // Keep MessageLoader state in sync
    messageLoader.current?.ingestLoadedChildren(parentId, children);




    // Optimistically update UI with immutable merge
    setMessages((prev) => {
      // 1단계: 일반 답글/대댓글 (emoticon이 없는 것) - 모두 같은 레벨에 표시
      const replies = children.filter(c => !c.emoticon);

      // 2단계: 리액션 (emoticon이 있는 것) - 각 메시지의 children으로 분류
      const reactions = children.filter(c => c.emoticon);


      // 3단계: 리액션을 replyToMessageId 기준으로 그룹화
      const reactionsByReplyTo = new Map<string, Message[]>();
      reactions.forEach(r => {
        const replyToMsgId = r.replyToMessageId;
        if (replyToMsgId) {
          const existing = reactionsByReplyTo.get(replyToMsgId) || [];
          existing.push(r);
          reactionsByReplyTo.set(replyToMsgId, existing);
        }
      });


      // 4단계: 답글을 UIMessage로 변환하면서 해당 답글의 리액션 포함
      const incoming = replies.map((c) => {
        const myReactions = reactionsByReplyTo.get(c.id) || [];
        return new UIMessage(c, myReactions.map(r => new UIMessage(r, [])));
      });

      return prev.map((m) => {
        if (m.msg.id !== parentId) return m;

        // Dedupe by id and merge reactions
        const map = new Map<string, UIMessage>();
        m.children.forEach((c) => map.set(c.msg.id, c));
        incoming.forEach((c) => {
          const existing = map.get(c.msg.id);
          if (existing) {
            // 기존 reactions와 새로운 reactions 병합
            const mergedReactions = [...existing.children];
            c.children.forEach(r => {
              if (!mergedReactions.find(e => e.msg.id === r.msg.id)) {
                mergedReactions.push(r);
              }
            });
            const merged = new UIMessage(c.msg, mergedReactions);
            map.set(c.msg.id, merged);
          } else {
            map.set(c.msg.id, c);
          }
        });

        // 시간순 정렬
        const merged = Array.from(map.values()).sort((a, b) => {
          const aTime = new Date(a.msg.created).getTime();
          const bTime = new Date(b.msg.created).getTime();
          return aTime - bTime;
        });

        return m.withChildren(merged);
      });
    });
  }, []);

  // 메시지 클릭
  const messageClicked = useCallback((uimsg: UIMessage) => {
    setCurrentMessage(uimsg);

    // 멘션 대상 설정 (스레드 ID 포함)
    setMentionTarget({
      user: uimsg.msg.user,
      messageId: uimsg.msg.id,
      threadId: uimsg.msg.parentMessageId ?? uimsg.msg.id
    });
  }, []);

  // 메시지 상태 - sendingMessage 포함
  const getMessageItemState = useCallback((m: UIMessage): MessageItemState => {
    // sending 상태 체크
    if (sendingMessage[m.msg.id]) {
      return 'new'; // sending 상태는 'new'로 표시 (스피너는 RoomMessageItem에서 처리)
    }
    if (m.msg.inProgress) {
      return 'new';
    }

    let itemState: MessageItemState = 'new';
    if (currentMessage?.msg.id === m.msg.id && state === 'playing') {
      itemState = 'playing';
    } else {
      itemState = m.isNew() ? 'new' : 'old';
    }
    return itemState;
  }, [currentMessage, state, sendingMessage]);

  // 재전송
  const resendMessage = useCallback((uimsg: UIMessage) => {
    // TODO: 구현
  }, []);

  // 필터
  const shouldShow = useCallback((msg: Message) => {
    if (type === 'photos') return msg.files?.length > 0 && msg.files[0].group === 'image';
    if (type === 'docs') return msg.files?.length > 0 && msg.files[0].group === 'doc';
    if (type === 'voice') return !!msg.recording;
    if (type === 'video') return !!msg.videoUrl;
    return true;
  }, [type]);

  // 타겟 메시지 - 메시지가 없으면 API로 로드 후 스크롤
  const targetMessage = useCallback(async (messageId: string | null, parentMessageId: string | null) => {
    if (!messageId) return;


    // 1. 이미 로드된 메시지인지 확인
    const notifMsg = messageLoader.current?.allMessages.find(m => m.id == messageId);

    if (notifMsg) {
      // 자식 메시지인 경우 스레드 펼치기
      if (notifMsg.parentMessageId) {
        addActiveThread(notifMsg.parentMessageId);
      }

      setHighlightedId(messageId);
      setTimeout(() => setHighlightedId(null), 3000);

      // DOM 스크롤 (재시도 로직 포함)
      const scrollToMessage = (retries = 5) => {
        const element = messageRefs.current[messageId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (retries > 0) {
          setTimeout(() => scrollToMessage(retries - 1), 200);
        } else {
          console.warn('[COLUMN-REVERSE] Could not find element after retries (memory):', messageId);
        }
      };
      // 스레드 펼침 후 DOM 렌더링 대기
      setTimeout(() => scrollToMessage(), 300);
      return;
    }

    // 2. 메시지가 없으면 API로 로드
    setInProgress(true);

    try {
      const msg = await Fire.msg.getMessage(messageId);
      if (!msg) {
        console.warn("[COLUMN-REVERSE] Message not found in API:", messageId);
        setInProgress(false);
        return;
      }

      let parentId = msg.parentMessageId || msg.id;
      let parentMsg = msg.parentMessageId
        ? await Fire.msg.getMessage(msg.parentMessageId)
        : msg;

      if (!parentMsg) {
        console.warn("[COLUMN-REVERSE] Parent message not found");
        setInProgress(false);
        return;
      }

      // 부모의 자식 메시지들 로드
      if (msg.parentMessageId) {
        const children = await Fire.msg.getChildrenMessages(My.user.id, parentId);
        messageLoader.current?.ingestLoadedChildren(parentId, children);
      }

      // 🔧 부모 메시지를 먼저 명시적으로 추가 (getMessagesAfter/getMessages는 해당 시간 미포함)
      await messageLoader.current?.ingestMessages([parentMsg]);

      // 부모 이후의 메시지들도 로드 (최근 메시지 컨텍스트)
      const afterMsgs = await Fire.msg.getMessagesAfter(My.user.id, room.id, new Date(parentMsg.created));
      if (afterMsgs.length > 0 && messageLoader.current) {
        await messageLoader.current.ingestMessages(afterMsgs);
      }

      // 부모 이전의 메시지들 로드
      const olderMsgs = await Fire.msg.getMessages(My.user.id, room.id, new Date(parentMsg.created), FireClientMsg.NrMsgsPerLoad);
      if (olderMsgs.length > 0 && messageLoader.current) {
        await messageLoader.current.ingestMessages(olderMsgs);
      }

      // UI 갱신
      const uimsgs = messageLoader.current?.getUIMessages() ?? [];
      setMessages(uimsgs);

      // 스레드 펼침
      if (msg.parentMessageId) {
        addActiveThread(msg.parentMessageId);
      }

      // 하이라이트 및 스크롤
      setHighlightedId(messageId);
      setTimeout(() => setHighlightedId(null), 3000);

      // 렌더링 완료 후 스크롤 (재시도 로직 강화)
      const scrollToMessage = (retries = 5) => {
        const element = messageRefs.current[messageId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (retries > 0) {
          setTimeout(() => scrollToMessage(retries - 1), 200);
        } else {
          console.warn('[COLUMN-REVERSE] Could not find element after retries:', messageId);
        }
      };
      // 스레드 펼침 후 DOM 렌더링 시간 확보 (400ms 대기)
      setTimeout(() => scrollToMessage(), 400);

    } catch (error) {
      console.error("[COLUMN-REVERSE] Error loading message:", error);
    } finally {
      setInProgress(false);
    }
  }, [room.id, addActiveThread]);
  targetMessageRef.current = targetMessage;

  // 알림 처리 - 리액션 하이라이트 포함
  useEffect(() => {
    if (notification && state === 'ready') {
      const messageId = notification.type === 'Emoticon'
        ? notification.replyToMessageId
        : notification.messageId;

      // 리액션 알림인 경우 해당 이모지 하이라이트
      const notifAny = notification as any;
      if (notification.type === 'Emoticon' && notifAny.emoticon && notification.replyToMessageId) {
        setHighlightedReaction({
          messageId: notification.replyToMessageId,
          emoticon: notifAny.emoticon
        });
        setTimeout(() => setHighlightedReaction(null), 3000);
      }

      targetMessage(messageId, notification.parentMessageId ?? null);

      // 🔧 알림 클릭 후 해당 알림 읽음 처리
      setTimeout(() => {
        resetNotificationsForRoomRef.current?.();
      }, 500);

      setNotification(null);
    }
  }, [notification, state, targetMessage, setNotification]);

  // 필터된 메시지
  const filteredMessages = messages.filter(m => shouldShow(m.msg));

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* 메시지 리스트 또는 스켈레톤 */}
      {state === 'loading' ? (
        <div
          className="flex-1 flex flex-col justify-end p-4 gap-3 overflow-hidden"
          style={contentWidth === 'min-width' ? {
            paddingLeft: 'max(16px, calc((100% - 700px) / 2))',
            paddingRight: 'max(16px, calc((100% - 700px) / 2))'
          } : undefined}
        >
          {[
            { nameW: '80px', lines: ['75%', '50%'] },
            { nameW: '100px', lines: ['90%'] },
            { nameW: '70px', lines: ['60%', '85%', '40%'] },
            { nameW: '90px', lines: ['80%'] },
            { nameW: '110px', lines: ['65%', '45%'] },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full skeleton-shimmer shrink-0" />
              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="h-3.5 skeleton-shimmer rounded-full" style={{ width: item.nameW }} />
                  <div className="h-2.5 skeleton-shimmer rounded-full" style={{ width: '50px' }} />
                </div>
                {item.lines.map((w, j) => (
                  <div key={j} className="h-3.5 skeleton-shimmer rounded-full" style={{ width: w }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
      /* 메시지 리스트 - column-reverse 핵심! */
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden pb-4"
        onScroll={handleScroll}
        style={{
          display: 'flex',
          flexDirection: 'column-reverse',  // 🔧 핵심!
          gap: '16px',
          overscrollBehavior: 'none',
          // min-width 모드: CSS padding으로 콘텐츠를 가운데 정렬
          // column-reverse의 direct children을 감싸는 wrapper를 추가하면 순서가 깨지므로
          // padding 방식으로 너비를 제한함 (스크롤바는 화면 가장자리 유지)
          ...(contentWidth === 'min-width' ? {
            paddingLeft: 'max(0px, calc((100% - 700px) / 2))',
            paddingRight: 'max(0px, calc((100% - 700px) / 2))'
          } : {})
        }}
      >
        {/* column-reverse에서는 DOM 순서가 역순으로 표시됨 */}
        {/* 따라서 최신 메시지(배열 끝)가 화면 하단에 표시 */}

        {messages.length === 0 && !inProgress ? (
          <RoomWelcomeMessage room={room} />
        ) : (
          <>
            {/* 
                         * column-reverse에서 DOM 순서가 역순으로 표시됨:
                         * - DOM 첫 번째 → 화면 하단
                         * - DOM 마지막 → 화면 상단
                         * 
                         * 메시지 배열은 [오래된...최신] 순서이므로
                         * 역순으로 렌더링해야 최신이 하단에, 오래된게 상단에 표시됨
                         */}
            {[...filteredMessages].reverse().map((message) => (
              <div
                key={`${message.msg.id}-${message.isLocal ? 'local' : 'server'}`}
                ref={(el) => messageRefs.current[message.msg.id] = el}
              >
                <RoomMessageItem
                  message={message}
                  room={room}
                  activeThreads={activeThreads}
                  toggleActiveThread={toggleActiveThread}
                  highlightedId={highlightedId}
                  highlightedReaction={highlightedReaction}
                  currentMessage={currentMessage}
                  messageRefs={messageRefs}
                  getNodeRef={getNodeRef}
                  getMessageItemState={getMessageItemState}
                  onMessageClick={messageClicked}
                  progressForFile={progressForFile}
                  onResendMessage={resendMessage}
                  mentionTarget={mentionTarget}
                  setMentionTarget={setMentionTarget}
                  forceUpdate={forceUpdate}
                  onTargetMessage={targetMessage}
                  onLoadChildren={handleLoadChildren}
                  onReactionChildrenChange={handleReactionChildrenChange}
                  roomTokens={roomTokens}
                />
              </div>
            ))}

            {/* 로딩 인디케이터 / 더 불러오기 (column-reverse에서 DOM 마지막 = 화면 상단) */}
            <div className="flex justify-center py-4">
              {inProgress ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              ) : noMoreMessages ? (
                <span className="text-muted-foreground text-sm"><Trans>No more messages</Trans></span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadOlderMessages}
                  className="text-muted-foreground hover:text-card-foreground"
                >
                  <Trans>Load more</Trans>
                </Button>
              )}
            </div>
          </>
        )}
      </div>
      )}

      {/* 메시지 입력 - flex 레이아웃 */}
      {type === 'none' && !Room.isArchivedOneToOne(room) && !room.isReadOnly && (
        <div className="flex-shrink-0 relative bg-background z-10 w-full border-t border-border pb-safe">
          <div className="w-full">
            {/* Thread Toast */}
            {threadToast && (
              <div
                className="absolute bottom-full left-6 right-6 mb-2 px-3 py-2 bg-background rounded-xl shadow-lg border border-border flex items-center gap-2 cursor-pointer hover:bg-muted transition-colors"
                onClick={() => {
                  targetMessage(threadToast.id, threadToast.parentMessageId);
                  dismissToast();
                }}
              >
                {threadToast.emoticon ? (
                  <>
                    <img src={`/assets/icon/ic-${threadToast.emoticon}-24.svg`} alt={threadToast.emoticon} className="w-8 h-8" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-meta-sm font-bold text-primary line-clamp-1">
                        <Trans>New reaction</Trans>
                      </span>
                      <span className="text-meta-sm text-card-foreground line-clamp-1">
                        {getDisplayName(resolveMessageUser(threadToast.user, room.users))}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <UserAvatar src={resolveMessageUser(threadToast.user, room.users).photoUrl} name={getDisplayName(resolveMessageUser(threadToast.user, room.users))} className="w-6 h-6 flex-shrink-0" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-meta-sm font-bold text-primary line-clamp-1">
                        <Trans>New reply</Trans>: {getDisplayName(resolveMessageUser(threadToast.user, room.users))}
                      </span>
                      <span className="text-meta-sm text-card-foreground line-clamp-1">
                        {Util.getTextAsPlain(threadToast.text)}
                      </span>
                    </div>
                  </>
                )}
                <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full flex-shrink-0 ml-auto" onClick={(e) => { e.stopPropagation(); dismissToast(); }}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}


            <MsgInputFooter
              room={room}
              parentMessage={currentMessage}
              messages={messages}
              onClearParent={addLocalMsg}
              onScrollToParent={handleScrollToParent}
              onProgressForFile={onProgressForFile}
              onSendedMessage={onSendedMessage}
              soribotsRef={soribotsRef}
            />
          </div>
        </div >
      )}
      {room.isReadOnly && (
        <div className="flex-shrink-0 bg-muted/50 border-t border-border px-4 py-3 text-center pb-safe">
          <p className="text-sm text-muted-foreground"><Trans>This channel is read-only.</Trans></p>
        </div>
      )}
    </div >
  );
};

export default memo(RoomContent);
